import { useEffect, useMemo, useRef, useState } from "react";
import { UI_TEXT, WEB_LINKS } from "../../constants/text";
import logo from "../../logo.png";
import { getDesktopValue, setDesktopValue } from "../../utils/desktopStore";
import { parseApiJson } from "../../utils/parseApiJson";
import { friendlyErrorMessage } from "../../utils/friendlyErrorMessage";
import { API_URL } from "../dashboard/constants";
import "./DesktopPage.css";

function formatClockPart(value) {
  return String(value).padStart(2, "0");
}

function clockPartsToSeconds(clock) {
  if (!clock) return 0;
  return (clock.hours || 0) * 3600 + (clock.minutes || 0) * 60 + (clock.seconds || 0);
}

function DesktopPage({ onLogout, currentUser, authToken }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [updateCheckMessage, setUpdateCheckMessage] = useState("");
  const [theme, setTheme] = useState("dark");
  const menuRef = useRef(null);
  const [localElapsed, setLocalElapsed] = useState(0);
  const [localBreakElapsed, setLocalBreakElapsed] = useState(0);
  const [sessionDisplayName, setSessionDisplayName] = useState(undefined);
  const [displayNameInput, setDisplayNameInput] = useState("");
  const [displayNameError, setDisplayNameError] = useState("");
  const [showAdminVerify, setShowAdminVerify] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminVerifyError, setAdminVerifyError] = useState("");
  const [isVerifyingAdmin, setIsVerifyingAdmin] = useState(false);
  const [macPermissions, setMacPermissions] = useState({ screen: true, accessibility: true });
  const [isRetrying, setIsRetrying] = useState(false);
  const timerSyncRef = useRef({ elapsed: 0, breakElapsed: 0, syncTime: Date.now(), isRunning: false, isOnBreak: false });

  useEffect(() => {
    let isMounted = true;
    async function checkMacPermissions() {
      if (!window.desktopMonitor?.getMacPermissionStatus) {
        return;
      }
      try {
        const status = await window.desktopMonitor.getMacPermissionStatus();
        if (isMounted && status) {
          setMacPermissions({ screen: status.screen !== false, accessibility: status.accessibility !== false });
        }
      } catch (_error) {
        // Leave last-known status as-is.
      }
    }
    checkMacPermissions();
    // Re-check when the window regains focus so the banner clears without a
    // full app restart once the user grants the permission in System Settings.
    window.addEventListener("focus", checkMacPermissions);
    return () => {
      isMounted = false;
      window.removeEventListener("focus", checkMacPermissions);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadSessionDisplayName() {
      if (currentUser?.accountType !== "shared") {
        if (isMounted) setSessionDisplayName(null);
        return;
      }
      const saved = await getDesktopValue("workivity-session-display-name", null);
      if (isMounted) setSessionDisplayName(saved || null);
    }
    loadSessionDisplayName();
    return () => {
      isMounted = false;
    };
  }, [currentUser?.accountType]);

  async function handleSubmitDisplayName(event) {
    event.preventDefault();
    const trimmedName = displayNameInput.trim();
    if (!trimmedName) {
      setDisplayNameError("Please enter your name to continue.");
      return;
    }
    await setDesktopValue("workivity-session-display-name", trimmedName);
    setSessionDisplayName(trimmedName);
  }

  function requestLogout() {
    setIsMenuOpen(false);
    if (currentUser?.accountType === "shared") {
      setAdminEmail("");
      setAdminPassword("");
      setAdminVerifyError("");
      setShowAdminVerify(true);
      return;
    }
    onLogout();
  }

  async function handleAdminVerifySubmit(event) {
    event.preventDefault();
    if (isVerifyingAdmin) return;
    setIsVerifyingAdmin(true);
    setAdminVerifyError("");
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: adminEmail.trim(), password: adminPassword }),
      });
      const payload = await parseApiJson(response);
      if (response.ok && payload?.ok) {
        setShowAdminVerify(false);
        onLogout();
        return;
      }
      setAdminVerifyError(payload?.message || UI_TEXT.adminVerify.genericError);
    } catch (_error) {
      setAdminVerifyError(UI_TEXT.adminVerify.genericError);
    } finally {
      setIsVerifyingAdmin(false);
    }
  }

  async function loadDetail(isMountedCheck = () => true) {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/detail`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });

      if (!response.ok) {
        throw new Error(UI_TEXT.errors.loadDashboard);
      }

      const payload = await parseApiJson(response);
      if (isMountedCheck()) {
        setData(payload);
        setError("");
      }
    } catch (loadError) {
      if (isMountedCheck()) {
        setError(friendlyErrorMessage(loadError));
      }
    }
  }

  useEffect(() => {
    let isMounted = true;

    function mountedCheck() {
      return isMounted;
    }

    loadDetail(mountedCheck);
    const intervalId = setInterval(() => loadDetail(mountedCheck), 30000);
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!data?.session) return;
    const { elapsedSeconds = 0, isClockedOut, onBreak, breakClock } = data.session;
    const breakElapsed = clockPartsToSeconds(breakClock);
    const isRunning = !isClockedOut && !onBreak;
    const isOnBreak = !isClockedOut && Boolean(onBreak);
    timerSyncRef.current = { elapsed: elapsedSeconds, breakElapsed, syncTime: Date.now(), isRunning, isOnBreak };
    setLocalElapsed(elapsedSeconds);
    setLocalBreakElapsed(breakElapsed);
  }, [data]);

  useEffect(() => {
    const tickId = setInterval(() => {
      const { elapsed, breakElapsed, syncTime, isRunning, isOnBreak } = timerSyncRef.current;
      const secondsPassed = Math.floor((Date.now() - syncTime) / 1000);
      if (isRunning) setLocalElapsed(elapsed + secondsPassed);
      if (isOnBreak) setLocalBreakElapsed(breakElapsed + secondsPassed);
    }, 1000);
    return () => clearInterval(tickId);
  }, []);

  // Reset timer exactly at midnight (Asia/Karachi) so the daily counter starts from 0
  useEffect(() => {
    function msUntilMidnightPKT() {
      const now = new Date();
      const timeParts = {};
      for (const p of new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Karachi",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).formatToParts(now)) {
        if (p.type !== "literal") timeParts[p.type] = Number(p.value);
      }
      const h = timeParts.hour === 24 ? 0 : (timeParts.hour || 0);
      const secondsSinceMidnight = h * 3600 + (timeParts.minute || 0) * 60 + (timeParts.second || 0);
      return Math.max(500, (86400 - secondsSinceMidnight) * 1000);
    }

    let timeoutId;
    function onMidnight() {
      timerSyncRef.current = {
        ...timerSyncRef.current,
        elapsed: 0,
        breakElapsed: 0,
        syncTime: Date.now(),
        isRunning: false,
        isOnBreak: false,
      };
      setLocalElapsed(0);
      setLocalBreakElapsed(0);
      loadDetail();
      timeoutId = setTimeout(onMidnight, msUntilMidnightPKT());
    }
    timeoutId = setTimeout(onMidnight, msUntilMidnightPKT());
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function loadTheme() {
      const themeKey = `workivity-theme:${currentUser?.email || "default"}`;
      const storedTheme = await getDesktopValue(themeKey, "dark");
      if (storedTheme === "dark" || storedTheme === "light") {
        setTheme(storedTheme);
      }
    }
    loadTheme();
  }, [currentUser?.email]);

  useEffect(() => {
    const themeKey = `workivity-theme:${currentUser?.email || "default"}`;
    setDesktopValue(themeKey, theme);
  }, [currentUser?.email, theme]);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (!menuRef.current || menuRef.current.contains(event.target)) {
        return;
      }
      setIsMenuOpen(false);
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    async function syncScreenshotCaptureFromOrg() {
      if (!window.desktopMonitor?.setScreenshotConfig) {
        return;
      }
      const sc = data?.screenCapture;
      if (!sc || typeof sc !== "object") {
        return;
      }
      await window.desktopMonitor.setScreenshotConfig({
        enabled: sc.enabled !== false,
        intervalMinutes: Number(sc.intervalMinutes) || 5,
      });
    }
    void syncScreenshotCaptureFromOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.screenCapture?.enabled, data?.screenCapture?.intervalMinutes]);

  useEffect(() => {
    async function syncIdleSettingsFromOrg() {
      if (!window.desktopMonitor?.setIdleSettings) {
        return;
      }
      const idle = data?.idleSettings;
      if (!idle || typeof idle !== "object") {
        return;
      }
      const intervalMinutes = Number(idle.intervalMinutes);
      const thresholdSeconds = Number(idle.thresholdSeconds);
      if (Number.isFinite(thresholdSeconds) && thresholdSeconds >= 1) {
        await window.desktopMonitor.setIdleSettings({
          intervalMinutes: Number.isFinite(intervalMinutes) ? intervalMinutes : 2,
          thresholdSeconds,
        });
      } else if (Number.isFinite(intervalMinutes) && intervalMinutes >= 1) {
        await window.desktopMonitor.setIdleSettings({ intervalMinutes });
      }
    }
    void syncIdleSettingsFromOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.idleSettings?.intervalMinutes, data?.idleSettings?.thresholdSeconds]);

  useEffect(() => {
    async function syncTrackingConditionFromOrg() {
      if (!window.desktopMonitor?.setTrackingCondition) {
        return;
      }
      const tc = data?.trackingCondition;
      if (!tc || typeof tc !== "string") {
        return;
      }
      await window.desktopMonitor.setTrackingCondition({ trackingCondition: tc });
    }
    void syncTrackingConditionFromOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.trackingCondition]);

  useEffect(() => {
    async function syncAppUsageConfigFromOrg() {
      if (!window.desktopMonitor?.setAppUsageConfig) {
        return;
      }
      const au = data?.appUsage;
      if (!au || typeof au !== "object") {
        return;
      }
      const intervalMinutes = Number(au.intervalMinutes);
      if (Number.isFinite(intervalMinutes) && intervalMinutes >= 1) {
        await window.desktopMonitor.setAppUsageConfig({ intervalMinutes });
      }
    }
    void syncAppUsageConfigFromOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.appUsage?.intervalMinutes]);

  async function handleBreakAction() {
    if (!data?.session || data.session.isClockedOut || isSubmittingAction) {
      return;
    }

    const nextOnBreak = !data.session.onBreak;
    setIsSubmittingAction(true);
    try {
      const endpoint = data.session.onBreak ? "resume-work" : "take-break";
      const response = await fetch(`${API_URL}/api/dashboard/${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(UI_TEXT.errors.updateBreakStatus);
      }

      const payload = await parseApiJson(response);
      const updatedSession = payload?.session || { ...data.session, onBreak: nextOnBreak };
      setData((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          session: updatedSession,
        };
      });
    } catch (actionError) {
      setError(friendlyErrorMessage(actionError));
    } finally {
      setIsSubmittingAction(false);
    }
  }

  async function handleClockAction() {
    if (!data?.session || isSubmittingAction) {
      return;
    }

    setIsSubmittingAction(true);
    try {
      const endpoint = data.session.isClockedOut ? "clock-in" : "clock-out";
      const response = await fetch(`${API_URL}/api/dashboard/${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(UI_TEXT.errors.updateClockStatus);
      }

      const payload = await parseApiJson(response);
      const updatedSession = payload?.session || {
        ...data.session,
        isClockedOut: data.session.isClockedOut ? false : true,
        onBreak: false,
      };
      setData((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          session: updatedSession,
        };
      });
    } catch (actionError) {
      setError(friendlyErrorMessage(actionError));
    } finally {
      setIsSubmittingAction(false);
    }
  }

  function openWebPage(url) {
    setIsMenuOpen(false);
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function openMacPermissionSettings(which) {
    window.desktopMonitor?.openMacPermissionSettings?.(which);
  }

  async function handleCheckForUpdate() {
    if (isCheckingUpdate || !window.desktopUpdater?.checkForUpdates) {
      return;
    }
    setIsCheckingUpdate(true);
    setUpdateCheckMessage("");
    try {
      const result = await window.desktopUpdater.checkForUpdates();
      if (result?.ok && !result.updateAvailable) {
        setUpdateCheckMessage(UI_TEXT.updateCheck.upToDate);
      } else if (!result?.ok) {
        setUpdateCheckMessage(UI_TEXT.updateCheck.failed);
      }
      // When an update is available, the main process opens the update window automatically.
    } catch (_error) {
      setUpdateCheckMessage(UI_TEXT.updateCheck.failed);
    } finally {
      setIsCheckingUpdate(false);
    }
  }

  const timer = useMemo(() => ({
    hours: formatClockPart(Math.floor(localElapsed / 3600)),
    minutes: formatClockPart(Math.floor((localElapsed % 3600) / 60)),
    seconds: formatClockPart(localElapsed % 60),
  }), [localElapsed]);

  const breakTimer = useMemo(() => ({
    hours: formatClockPart(Math.floor(localBreakElapsed / 3600)),
    minutes: formatClockPart(Math.floor((localBreakElapsed % 3600) / 60)),
    seconds: formatClockPart(localBreakElapsed % 60),
  }), [localBreakElapsed]);

  const displayName = useMemo(() => {
    if (currentUser?.accountType === "shared") {
      return "Digitechio";
    }
    const fromSession = `${currentUser?.name || ""} ${currentUser?.surname || ""}`.trim();
    return fromSession || data?.user?.name || "User";
  }, [currentUser?.accountType, currentUser?.name, currentUser?.surname, data?.user?.name]);

  const firstName = useMemo(() => {
    return displayName.split(" ")[0] || "User";
  }, [displayName]);

  const displayCompany = useMemo(() => {
    return currentUser?.team || data?.user?.company || "Team";
  }, [currentUser?.team, data?.user?.company]);

  const displayRole = useMemo(() => {
    return currentUser?.role || data?.user?.role || "Employee";
  }, [currentUser?.role, data?.user?.role]);

  if (showAdminVerify) {
    return (
      <main className={`desktop-shell ${theme}-theme`}>
        <div className="brand-row">
          <img className="desktop-logo" src={logo} alt={UI_TEXT.brand} />
        </div>
        <form className="display-name-prompt" onSubmit={handleAdminVerifySubmit}>
          <h2>{UI_TEXT.adminVerify.heading}</h2>
          <p>{UI_TEXT.adminVerify.description}</p>
          <input
            type="email"
            value={adminEmail}
            onChange={(event) => {
              setAdminEmail(event.target.value);
              setAdminVerifyError("");
            }}
            placeholder={UI_TEXT.adminVerify.emailPlaceholder}
            autoFocus
            required
          />
          <input
            type="password"
            value={adminPassword}
            onChange={(event) => {
              setAdminPassword(event.target.value);
              setAdminVerifyError("");
            }}
            placeholder={UI_TEXT.adminVerify.passwordPlaceholder}
            required
          />
          {adminVerifyError ? <p className="form-error">{adminVerifyError}</p> : null}
          <div className="display-name-prompt-actions">
            <button
              type="button"
              className="back-btn"
              onClick={() => setShowAdminVerify(false)}
              disabled={isVerifyingAdmin}
            >
              {UI_TEXT.adminVerify.cancel}
            </button>
            <button type="submit" disabled={isVerifyingAdmin}>
              {isVerifyingAdmin ? UI_TEXT.adminVerify.submitting : UI_TEXT.adminVerify.submit}
            </button>
          </div>
        </form>
      </main>
    );
  }

  if (currentUser?.accountType === "shared") {
    if (sessionDisplayName === undefined) {
      return <main className={`desktop-shell ${theme}-theme`}>{UI_TEXT.loading}</main>;
    }
    if (!sessionDisplayName) {
      return (
        <main className={`desktop-shell ${theme}-theme`}>
          <div className="brand-row">
            <img className="desktop-logo" src={logo} alt={UI_TEXT.brand} />
          </div>
          <form className="display-name-prompt" onSubmit={handleSubmitDisplayName}>
            <h2>Who&rsquo;s using this device?</h2>
            <p>This is a shared account. Enter your name so your activity is labeled correctly.</p>
            <input
              type="text"
              value={displayNameInput}
              onChange={(event) => {
                setDisplayNameInput(event.target.value);
                setDisplayNameError("");
              }}
              placeholder="Your name"
              autoFocus
              required
            />
            {displayNameError ? <p className="form-error">{displayNameError}</p> : null}
            <div className="display-name-prompt-actions">
              <button type="button" className="back-btn" onClick={requestLogout}>
                Back
              </button>
              <button type="submit">Continue</button>
            </div>
          </form>
        </main>
      );
    }
  }

  if (error) {
    return (
      <main className={`desktop-shell ${theme}-theme`}>
        <div className="status-screen">
          <img className="desktop-logo" src={logo} alt={UI_TEXT.brand} />
          <div className="status-screen-icon is-error" aria-hidden="true">
            ⚠
          </div>
          <h2>{UI_TEXT.errorPrefix}</h2>
          <p>{error}</p>
          <button
            type="button"
            className="status-screen-retry"
            disabled={isRetrying}
            onClick={async () => {
              setIsRetrying(true);
              await loadDetail();
              setIsRetrying(false);
            }}
          >
            {isRetrying ? "Retrying..." : "Retry"}
          </button>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className={`desktop-shell ${theme}-theme`}>
        <div className="status-screen">
          <img className="desktop-logo" src={logo} alt={UI_TEXT.brand} />
          <div className="status-screen-icon is-loading" aria-hidden="true">
            <span className="status-spinner" />
          </div>
          <p>{UI_TEXT.loading}</p>
        </div>
      </main>
    );
  }

  const macPermissionsMissing = !macPermissions.screen || !macPermissions.accessibility;

  return (
    <main className={`desktop-shell ${theme}-theme`}>
      {macPermissionsMissing ? (
        <div className="menu-update-msg mac-permission-banner">
          {!macPermissions.screen ? (
            <div>
              Screen Recording permission is needed for screenshots.{" "}
              <button type="button" className="link-btn" onClick={() => openMacPermissionSettings("screen")}>
                Open Settings
              </button>
            </div>
          ) : null}
          {!macPermissions.accessibility ? (
            <div>
              Accessibility permission is needed for app/window tracking.{" "}
              <button type="button" className="link-btn" onClick={() => openMacPermissionSettings("accessibility")}>
                Open Settings
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="brand-row">
        <img className="desktop-logo" src={logo} alt={UI_TEXT.brand} />
        <div className="menu-wrap" ref={menuRef}>
          <button
            className="icon-btn"
            type="button"
            aria-label={UI_TEXT.menuAriaLabel}
            onClick={() =>
              setIsMenuOpen((prev) => {
                const next = !prev;
                if (next) {
                  setUpdateCheckMessage("");
                }
                return next;
              })
            }
            aria-expanded={isMenuOpen}
          >
            {UI_TEXT.menuTrigger}
          </button>
          {isMenuOpen ? (
            <div className="menu-dropdown">
              <div className="menu-org">
                <strong>{displayCompany}</strong>
                <span>{displayRole}</span>
              </div>
              <button type="button" onClick={() => openWebPage(WEB_LINKS.activities)}>
                {UI_TEXT.menuItems.activities}
              </button>
              <button type="button" onClick={() => openWebPage(WEB_LINKS.settings)}>
                {UI_TEXT.menuItems.settings}
              </button>
              <button type="button" onClick={handleCheckForUpdate} disabled={isCheckingUpdate}>
                {isCheckingUpdate ? UI_TEXT.menuItems.checkingForUpdate : UI_TEXT.menuItems.checkForUpdate}
              </button>
              {updateCheckMessage ? <div className="menu-update-msg">{updateCheckMessage}</div> : null}
              <button type="button" onClick={requestLogout}>
                {UI_TEXT.menuItems.signOut}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <section className="user-card">
        <div className="avatar" style={{ background: data.user.avatarColor }}>
          {displayName.charAt(0)}
        </div>
        <div>
          <h2>{displayName}</h2>
          <p>{displayCompany}</p>
          <span>{displayRole}</span>
        </div>
      </section>

      <section className="center-panel">
        <h3>
          {UI_TEXT.welcomePrefix} <strong>{firstName}</strong>
        </h3>
        <div
          className={`status${
            data.session.alwaysOnBreak ? " status--url-break" : data.session.alwaysWorking ? " status--always-working" : ""
          }`}
        >
          {data.session.statusLabel}
        </div>

        <div className="timer-grid">
          <div>
            <small>{UI_TEXT.timeLabels.hours}</small>
            <p>{timer.hours}</p>
          </div>
          <div className="colon">:</div>
          <div>
            <small>{UI_TEXT.timeLabels.minutes}</small>
            <p>{timer.minutes}</p>
          </div>
          <div className="colon">:</div>
          <div>
            <small>{UI_TEXT.timeLabels.seconds}</small>
            <p>{timer.seconds}</p>
          </div>
        </div>

        {data.session.onBreak ? (
          <div className="break-duration">
            <small>{UI_TEXT.breakRunningFor}</small>
            <p>
              {breakTimer.hours} : {breakTimer.minutes} : {breakTimer.seconds}
            </p>
          </div>
        ) : null}

        <div className="actions">
          <button
            className={data.session.isClockedOut ? "clockin-btn" : "break-btn"}
            type="button"
            onClick={data.session.isClockedOut ? handleClockAction : handleBreakAction}
            disabled={isSubmittingAction}
          >
            {data.session.isClockedOut
              ? UI_TEXT.actions.clockIn
              : data.session.onBreak
                ? UI_TEXT.actions.resumeWork
                : UI_TEXT.actions.takeBreak}
          </button>
          <button
            className="clockout-btn"
            type="button"
            onClick={handleClockAction}
            disabled={isSubmittingAction || data.session.isClockedOut}
          >
            {UI_TEXT.actions.clockOut}
          </button>
        </div>

        <select defaultValue={data.session.note}>
          <option>{data.session.note || UI_TEXT.workNotePlaceholder}</option>
        </select>
      </section>

      <button
        className="theme-toggle"
        type="button"
        onClick={() => setTheme((prev) => (prev === "dark" ? "light" : "dark"))}
        aria-label={UI_TEXT.theme.ariaLabel}
        title={theme === "dark" ? UI_TEXT.theme.darkTitle : UI_TEXT.theme.lightTitle}
      >
        <span className="theme-toggle-track">
          <span className={`theme-toggle-thumb ${theme === "dark" ? "right" : "left"}`}>
            {theme === "dark" ? UI_TEXT.theme.darkIcon : UI_TEXT.theme.lightIcon}
          </span>
        </span>
      </button>
    </main>
  );
}

export default DesktopPage;
